import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { handleLocationError } from '@/features/marquee/components/location-toasts'
import { ResponsiveConfirmDialog } from '@/features/marquee/components/responsive-confirm-dialog'
import { makeQueryKeys } from '@/features/marquee/query-keys'
import {
	deactivateLocation,
	type MarqueeLocation,
	reactivateLocation
} from '@/sdk'
import { Trans, useTranslation } from '@/translations/i18n'

export type LocationConfirmAction = 'deactivate' | 'reactivate'

interface LocationConfirmDialogProps {
	action: LocationConfirmAction
	location: MarqueeLocation
	onClose: () => void
}

/**
 * Deactivate / reactivate a location, confirmed before it fires. Worded as
 * taking the place out of the event form's dropdown, never as a delete: the
 * events already using it keep it (OPSSTD-10100).
 *
 * Controlled by the page and mounted only while open, like the Team one, so
 * the deactivate reached from the edit form is the same one the menu opens.
 * Both writes refetch every locations list, the event form's included.
 */
function LocationConfirmDialog({
	action,
	location,
	onClose
}: LocationConfirmDialogProps) {
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	const { name } = location

	const confirm = useMutation({
		mutationFn: async () => {
			const path = { locationId: location.id }
			await (action === 'deactivate'
				? deactivateLocation({ path })
				: reactivateLocation({ path }))
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: makeQueryKeys.marquee.locations.all()
			})
			toast.success(
				action === 'deactivate'
					? t('marquee:locations_deactivated_toast', { name })
					: t('marquee:locations_reactivated_toast', { name })
			)
			onClose()
		},
		onError: (error) => handleLocationError(error)
	})

	const shared = {
		isPending: confirm.isPending,
		onConfirm: () => confirm.mutate(),
		onOpenChange: (open: boolean) => {
			if (!open) {
				onClose()
			}
		},
		open: true,
		showCloseButton: true,
		children: null
	}

	if (action === 'reactivate') {
		return (
			<ResponsiveConfirmDialog
				{...shared}
				confirmLabel={t('marquee:locations_action_reactivate')}
				description={t(
					'marquee:locations_reactivate_dialog_description',
					{ count: location.event_count }
				)}
				title={t('marquee:locations_reactivate_dialog_title', { name })}
			/>
		)
	}

	return (
		<ResponsiveConfirmDialog
			{...shared}
			body={<DeactivateConsequences location={location} />}
			confirmLabel={t('marquee:locations_action_deactivate')}
			description={t('marquee:locations_deactivate_dialog_description', {
				name
			})}
			title={t('marquee:locations_deactivate_dialog_title', { name })}
		/>
	)
}

/**
 * What the deactivation does and does not touch, as the design has it. The
 * events line is left out when no event uses the location.
 */
function DeactivateConsequences({ location }: { location: MarqueeLocation }) {
	const { t } = useTranslation()

	return (
		<div className='mt-6 flex flex-col gap-2 rounded-2xl bg-grey-100 p-4 text-sm/4.5'>
			<p className='font-medium text-grey-1000 text-md/5'>
				{t('marquee:locations_deactivate_what_happens')}
			</p>
			<ul className='ml-5 list-disc text-neutral-500'>
				<li>{t('marquee:locations_deactivate_hidden_from_form')}</li>
				{location.event_count > 0 && (
					<li>
						<Trans
							components={{
								strong: <strong className='font-medium' />
							}}
							i18nKey='marquee:locations_deactivate_events_kept'
							values={{ count: location.event_count }}
						/>
					</li>
				)}
				<li>{t('marquee:locations_deactivate_can_reactivate')}</li>
			</ul>
		</div>
	)
}

export { LocationConfirmDialog }
